// 積読リスト画面
// status === '積読' の本だけを集めて表示し、各書籍に「読書中に変更」ボタンを設ける。
// 読書中に変更されると library (ホーム) に表示されるようになる。
// AI に「優先度」を提案させると、各書籍カードに高/中/低と理由を表示する。
import {
  escapeHtml,
  screenHead,
  showScreen,
  showLoading,
  hideLoading,
  toast,
  fmtDate,
} from '../ui.js';
import { getAllBooks, upsertBook, saveAiHistory } from '../cache.js';
import { illustrationPathFor, fetchTbrPriority } from '../api.js';
import { showBookDetailScreen } from './book-detail.js';

// 画面内で最後に取得した優先度マップ。再 render 後も復元するためモジュールスコープに保持。
let priorityMap = null; // { [bookId]: { level, reason, order } } | null
let prioritySummary = '';

export async function showTbrScreen() {
  const books = await getAllBooks();
  const tbr = books
    .filter((b) => b.status === '積読')
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
  renderList(tbr);
}

function renderList(books) {
  if (!books.length) {
    const screen = showScreen(
      'tbr',
      `${screenHead('積読リスト')}<p class="empty">まだ積読書籍はありません。<br/>「購入予定リスト」で「購入済み」を押した本や、Notion で status=積読 にした本がここに表示されます。</p>`
    );
    screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
    return;
  }

  // 優先度がある場合は order でソート、なければ updatedAt 降順
  let sorted = books.slice();
  if (priorityMap) {
    sorted.sort((a, b) => {
      const pa = priorityMap[a.id]?.order ?? 999;
      const pb = priorityMap[b.id]?.order ?? 999;
      return pa - pb;
    });
  }

  const html = `
    ${screenHead('積読リスト')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      ${books.length} 冊が積読中です。読み始めたら「読書中に変更」を押すと、ホームの library に並びます。
    </p>
    <button class="btn btn--ink btn--block" id="btn-ai-priority" type="button" style="margin-bottom:20px;">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>${priorityMap ? 'AI 優先度を再計算する' : 'AI に優先度を提案させる'}</span>
    </button>
    ${prioritySummary ? `<div style="margin:0 0 20px;padding:14px 16px;border:1px solid var(--line);background:var(--paper);">
      <p style="margin:0 0 4px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">ai summary</p>
      <p style="margin:0;font-family:var(--font-serif);font-size:13px;line-height:1.7;color:var(--dim);">${escapeHtml(prioritySummary)}</p>
    </div>` : ''}
    <ul class="book-list" style="list-style:none;padding:0;margin:0;">
      ${sorted.map((b) => renderItem(b)).join('')}
    </ul>
  `;
  const screen = showScreen('tbr', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());

  screen.querySelector('#btn-ai-priority').addEventListener('click', async () => {
    await runPriority(sorted);
  });

  screen.querySelectorAll('[data-tbr-item]').forEach((li) => {
    const id = li.dataset.tbrItem;
    li.querySelector('[data-open]').addEventListener('click', () => {
      window.history.pushState({ screen: 'book-detail' }, '', `#book/${id}`);
      showBookDetailScreen(id);
    });
    li.querySelector('[data-start-reading]').addEventListener('click', async (e) => {
      e.stopPropagation();
      await startReading(id);
      // 読書中になった本は priorityMap からも除外
      if (priorityMap) delete priorityMap[id];
      const refreshed = await getAllBooks();
      const tbr = refreshed
        .filter((b) => b.status === '積読')
        .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
      renderList(tbr);
    });
  });
}

async function runPriority(tbrBooks) {
  const all = await getAllBooks();
  const history = all
    .filter((b) => b.status === '読了' || b.status === '読書中')
    .map((b) => ({
      name: b.name,
      author: b.author,
      genre: b.genre,
      note: (b.note || '').slice(0, 800),
      rating: b.rating,
    }));
  const tbrPayload = tbrBooks.map((b) => ({
    id: b.id,
    name: b.name,
    author: b.author,
    genre: b.genre,
    note: (b.note || '').slice(0, 400),
  }));
  showLoading('AI が読書優先度を判定中…');
  try {
    const result = await fetchTbrPriority(tbrPayload, history);
    priorityMap = {};
    for (const r of result.ranked || []) {
      priorityMap[r.id] = { level: r.level, reason: r.reason, order: r.order };
    }
    prioritySummary = result.summary || '';
    await saveAiHistory({
      type: 'tbr-priority',
      title: `${tbrBooks.length} 冊の優先度`,
      context: { tbrIds: tbrBooks.map((b) => b.id) },
      payload: result,
    });
    toast('優先度を更新しました');
    renderList(tbrBooks);
  } catch (e) {
    console.error(e);
    toast('優先度の判定に失敗しました');
  } finally {
    hideLoading();
  }
}

async function startReading(bookId) {
  const all = await getAllBooks();
  const book = all.find((b) => b.id === bookId);
  if (!book) return;
  const today = new Date().toISOString().slice(0, 10);
  const updated = {
    ...book,
    status: '読書中',
    startDate: book.startDate || today,
    updatedAt: new Date().toISOString(),
  };
  await upsertBook(updated);
  toast('読書中に変更しました');
}

function renderItem(b) {
  const initial = (b.name && b.name[0]) || '?';
  const coverSrc = b.hasIllustration
    ? illustrationPathFor(b.id)
    : b.illustrationDataUrl
    ? b.illustrationDataUrl
    : b.illustrationBlob
    ? URL.createObjectURL(b.illustrationBlob)
    : b.coverUrl
    ? b.coverUrl
    : null;
  const coverHtml = coverSrc
    ? `<img class="book-cover" src="${escapeHtml(coverSrc)}" alt="" loading="lazy" referrerpolicy="no-referrer" />`
    : `<div class="book-cover">${escapeHtml(initial)}</div>`;
  const tags = (b.genre || []).map((g) => `<span class="book-meta__tag">${escapeHtml(g)}</span>`).join('');
  const addedDate = b.createdAt ? fmtDate(b.createdAt) : '';
  const localBadge = b.localOnly ? '<span class="book-meta__tag">手動追加</span>' : '';
  const pri = priorityMap?.[b.id];
  const priorityHtml = pri
    ? `
      <div style="margin-top:4px;padding:10px 12px;border:1px solid var(--line);background:var(--paper);">
        <div style="display:flex;align-items:baseline;gap:10px;margin-bottom:6px;">
          <span style="font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">priority</span>
          <span style="font-family:var(--font-serif);font-size:15px;font-weight:500;">${escapeHtml(pri.level || '')}</span>
          ${pri.order ? `<span style="font-family:var(--font-mono);font-size:10px;color:var(--mute);">#${pri.order}</span>` : ''}
        </div>
        ${pri.reason ? `<p style="margin:0;font-family:var(--font-serif);font-size:12.5px;line-height:1.7;color:var(--dim);">${escapeHtml(pri.reason)}</p>` : ''}
      </div>
    `
    : '';
  return `
    <li class="book-item" data-tbr-item="${escapeHtml(b.id)}" style="flex-direction:column;align-items:stretch;gap:10px;">
      <div data-open style="display:flex;align-items:center;gap:14px;cursor:pointer;">
        ${coverHtml}
        <div class="book-body" style="flex:1;min-width:0;">
          <p class="book-title">${escapeHtml(b.name)}</p>
          <p class="book-author">${escapeHtml(b.author || '著者未登録')}</p>
          <div class="book-meta">
            ${localBadge}
            ${tags}
            ${addedDate ? `<span class="book-meta__tag" style="font-family:var(--font-mono);font-size:9px;letter-spacing:0.08em;">追加 ${escapeHtml(addedDate)}</span>` : ''}
          </div>
        </div>
      </div>
      ${priorityHtml}
      <button data-start-reading class="btn btn--ink btn--small btn--block" type="button">
        <svg class="icon"><use href="#i-open-book"/></svg>
        <span>読書中に変更</span>
      </button>
    </li>
  `;
}
